import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import type { Locale } from '../types/locale';
import { locales } from '../data/locales';
import { useDismiss } from '../hooks/useDismiss';
import { localeMeta } from '../utils/locale';
type LanguageMenuProps = {
  value: Locale;
  onChange: (next: Locale) => void;
  label: string;
  tone?: 'light' | 'dark';
  placement?: 'down' | 'up';
  // Field style shows a check before the current language, like a form select
  variant?: 'field' | 'link';
  className?: string;
};
const ease = [0.22, 1, 0.36, 1] as const;

// Languages are always listed in their own name and script, whatever language the page is in
export function LanguageMenu({
  value,
  onChange,
  label,
  tone = 'light',
  placement = 'down',
  variant = 'field',
  className = ''
}: LanguageMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const current = localeMeta(value);
  const dark = tone === 'dark';
  const panel = dark ? 'border-paper/15 bg-ink text-paper shadow-[0_16px_40px_-16px_rgba(0,0,0,0.6)]' : 'border-line bg-paper text-ink shadow-[0_18px_40px_-20px_rgba(0,0,0,0.25)]';
  return <div ref={ref} className={`relative ${variant === 'field' ? 'block' : 'inline-block'} ${className}`} data-id="element-632" data-mp="3quwdj67t2icg">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} aria-label={`${label}: ${current.nativeName}`} onClick={() => setOpen(o => !o)} className={variant === 'field' ? `flex h-[42px] w-full items-center gap-3 px-4 text-start text-[14px] transition-colors duration-200 ${dark ? 'bg-paper/10 text-paper hover:bg-paper/15' : `text-ink ${open ? 'bg-[#E2E2E2]' : 'bg-ash hover:bg-[#E2E2E2]'}`}` : 'inline-flex items-center gap-1.5 text-small font-medium transition-opacity duration-150 hover:opacity-60'} data-id="element-633" data-mp="3m4g19pfq12ro">
        {variant === 'field' && <CheckIcon className="h-3.5 w-3.5 shrink-0 opacity-60" strokeWidth={1.6} aria-hidden="true" data-id="element-634" data-mp="2jk1ugky8km7t" />}
        <span lang={current.code} dir={current.dir} className={variant === 'field' ? 'flex-1 select-none truncate text-start font-medium' : ''} data-id="element-635" data-mp="2oi3gneqyl1ni">
          {current.nativeName}
        </span>
        <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-636" data-mp="3h6r4ni33e54r" />
      </button>

      <AnimatePresence data-id="element-637" data-mp="37feagjttn2vk">
        {open && <motion.ul role="listbox" aria-label={label} initial={{
        opacity: 0,
        y: placement === 'up' ? 4 : -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: placement === 'up' ? 4 : -4
      }} transition={{
        duration: 0.22,
        ease
      }} className={`absolute left-0 z-50 min-w-full border py-2 ${variant === 'link' ? 'w-[220px]' : ''} ${placement === 'up' ? 'bottom-full mb-3' : 'top-full mt-2'} ${panel}`} data-id="element-638" data-mp="3v6s4cu7mf6in">
            {locales.map(l => {
          const selected = l.code === value;
          return <li key={l.code} role="option" aria-selected={selected} data-id="element-639" data-mp="15k75vsjuc8tm">
                  <button type="button" onClick={() => {
              setOpen(false);
              onChange(l.code);
            }} className={`flex w-full items-center justify-between gap-3 px-5 py-2.5 text-[14px] transition-colors duration-200 ${dark ? 'hover:bg-paper/10' : 'hover:bg-mist'} ${selected ? 'font-medium' : dark ? 'font-light text-paper/80' : 'font-light text-ink/75'}`} data-id="element-640" data-mp="10lvuveht7s9t">
                    <span lang={l.code} dir={l.dir} data-id="element-641" data-mp="3gcyr890vsnxm">
                      {l.nativeName}
                    </span>
                    {selected && <CheckIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-642" data-mp="3q9bfushkkbli" />}
                  </button>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}